<!-- biome-ignore-all lint/a11y/noStaticElementInteractions: the role is bound dynamically to combobox and the box only forwards focus to it -->
<script lang="ts" setup>
  import { useLocale } from '@tundr/i18n';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { computed, ref, useAttrs } from 'vue';
  import { FontIcon } from '../../../FontIcon';
  import { visuallyHiddenClass } from '../../../_shared/styles/visually-hidden.css';
  import { Loader, LoaderSize } from '../../../Loader';
  import Tag from '../../../Tag/Tag.vue';
  import { TagVariant } from '../../../Tag/Tag.types';
  import { FieldSize } from '../../FormField';
  import type { FieldSizeType } from '../../FormField';
  import type { SelectOption } from '../Select.types';
  import { selectTriggerClass, selectTriggerSmallClass } from '../Select.css';
  import SelectSearchField from './SelectSearchField.vue';
  import {
    tagListClass,
    triggerComboboxClass,
    triggerComboboxFillClass,
    triggerChevronOpenClass,
    triggerDisabledTextClass,
    triggerIndicatorClass,
    triggerMainClass,
    triggerRowClass,
    triggerPlaceholderClass,
    triggerValueClass,
    triggerValueSmallClass,
  } from './SelectTrigger.css';

  const props = defineProps<{
    selected: SelectOption[];
    labelOf: (option: SelectOption) => string;
    keyOf: (option: SelectOption) => string | number;
    multiple?: boolean;
    query?: string;
    displayValue?: string;
    placeholder?: string;
    searchable?: boolean;
    isOpen?: boolean;
    disabled?: boolean;
    loading?: boolean;
    maxVisibleTags?: number;
    size?: FieldSizeType;
    fieldId: string;
    panelId: string;
    labelId?: string;
    describedBy?: string;
    hasError?: boolean;
    testId?: string;
    activeDescendantId?: string;
  }>();

  defineOptions({ inheritAttrs: false });

  const emit = defineEmits<{
    remove: [option: SelectOption];
    'update:query': [query: string];
    navigate: [event: KeyboardEvent];
  }>();

  const { t } = useLocale('ds-components');

  const attrs = useAttrs();

  const OMITTED_TRIGGER_ATTRS = new Set([
    'aria-haspopup',
    'aria-expanded',
    'aria-controls',
    'type',
  ]);

  const boxAttrs = () =>
    Object.fromEntries(
      Object.entries(attrs).filter(
        ([name]) => !OMITTED_TRIGGER_ATTRS.has(name),
      ),
    );

  const boxEl = ref<HTMLElement | null>(null);
  const comboboxEl = ref<HTMLElement | null>(null);

  const visibleTags = computed(() =>
    props.maxVisibleTags === undefined
      ? props.selected
      : props.selected.slice(0, props.maxVisibleTags),
  );

  const hiddenTagCount = computed(
    () => props.selected.length - visibleTags.value.length,
  );

  const showSearch = computed(
    () => props.searchable && props.isOpen && !props.disabled,
  );

  const searchIsInline = computed(
    () => showSearch.value && props.selected.length === 0,
  );

  const isSmall = computed<boolean>(() => props.size === FieldSize.SM);

  const hasTags = computed(() => props.multiple && props.selected.length > 0);

  const showValueText = computed(() => !searchIsInline.value && !hasTags.value);

  const comboboxIsActive = computed(() => !showSearch.value);

  const searchInputAttrs = computed(() => ({
    role: 'combobox',
    'aria-autocomplete': 'list',
    'aria-haspopup': 'listbox',
    'aria-controls': props.panelId,
    'aria-expanded': true,
    'aria-activedescendant': props.activeDescendantId,
    'aria-labelledby': props.labelId,
    'aria-describedby': props.describedBy,
    'aria-invalid': props.hasError ? 'true' : undefined,
  }));

  const searchInputEl = () =>
    boxEl.value?.querySelector<HTMLInputElement>('input') ?? null;

  const onBoxFocus = () => {
    (showSearch.value ? searchInputEl() : comboboxEl.value)?.focus();
  };

  defineExpose({
    searchInputEl,
    comboboxEl: () => comboboxEl.value,
  });
</script>

<template>
  <div
    ref="boxEl"
    :class="[selectTriggerClass, isSmall && selectTriggerSmallClass]"
    v-bind="boxAttrs()"
    :data-testid="testId"
    :data-invalid="hasError ? '' : undefined"
    :data-disabled="disabled ? '' : undefined"
    :tabindex="disabled ? undefined : -1"
    @focus="onBoxFocus"
  >
    <div :class="triggerRowClass" data-test-id="select-trigger-row">
      <div v-if="!showValueText" :class="triggerMainClass">
        <SelectSearchField
          v-if="searchIsInline"
          inline
          :query="query"
          :input-attrs="searchInputAttrs"
          @update:query="(value) => emit('update:query', value)"
          @navigate="(event) => emit('navigate', event)"
        />

        <div v-else :class="tagListClass">
          <Tag
            v-for="option in visibleTags"
            :key="keyOf(option)"
            :variant="TagVariant.SELECTION"
            :remove-aria-label="
            t('select.remove_tag_aria_label', { label: labelOf(option) })
          "
            @remove="emit('remove', option)"
            >{{
              labelOf(option)
            }}</Tag
          >
          <Tag v-if="hiddenTagCount > 0" :variant="TagVariant.DISPLAY"
            >+{{ hiddenTagCount }}</Tag
          >
        </div>
      </div>

      <div
        :id="fieldId"
        ref="comboboxEl"
        :class="[triggerComboboxClass, showValueText && triggerComboboxFillClass]"
        data-test-id="select-trigger"
        :data-invalid="hasError ? '' : undefined"
        :data-disabled="disabled ? '' : undefined"
        :role="comboboxIsActive ? 'combobox' : undefined"
        :aria-haspopup="comboboxIsActive ? 'listbox' : undefined"
        :aria-controls="comboboxIsActive ? panelId : undefined"
        :aria-expanded="comboboxIsActive ? isOpen : undefined"
        :aria-activedescendant="comboboxIsActive ? activeDescendantId : undefined"
        :aria-labelledby="comboboxIsActive ? labelId : undefined"
        :aria-describedby="comboboxIsActive ? describedBy : undefined"
        :aria-invalid="comboboxIsActive && hasError ? 'true' : undefined"
        :aria-disabled="comboboxIsActive && disabled ? 'true' : undefined"
        :aria-hidden="comboboxIsActive ? undefined : 'true'"
        :tabindex="disabled || !comboboxIsActive ? -1 : 0"
        @keydown="(event) => emit('navigate', event)"
      >
        <span
          v-if="showValueText"
          :class="[
          triggerValueClass,
          isSmall && triggerValueSmallClass,
          { [triggerPlaceholderClass]: !displayValue },
          { [triggerDisabledTextClass]: disabled },
        ]"
          >{{
            displayValue || placeholder
          }}</span
        >
        <span v-else-if="hasTags" :class="visuallyHiddenClass">{{
          displayValue
        }}</span>

        <span :class="triggerIndicatorClass">
          <Loader v-if="loading" :size="LoaderSize.SM" />
          <FontIcon
            v-else
            :icon-name="TUNDR_ICONS.CHEVRON_DOWN"
            :class="isOpen ? triggerChevronOpenClass : undefined"
          />
        </span>
      </div>
    </div>

    <SelectSearchField
      v-if="showSearch && !searchIsInline"
      test-id="select-search-row"
      :query="query"
      :input-attrs="searchInputAttrs"
      @update:query="(value) => emit('update:query', value)"
      @navigate="(event) => emit('navigate', event)"
    />
  </div>
</template>
